import { ApiError } from './types';

/**
 * Offline fronta akcí (fáze F3.2).
 *
 * PROČ VŮBEC EXISTUJE
 * Ve skladu bývá slabý signál. Bez fronty tlačítko „Nahlásit dispečerům"
 * v nejhorší chvíli tiše selže – právě tehdy, kdy na hlášení nejvíc záleží.
 *
 * PROČ NE BACKGROUND SYNC
 * Background Sync API umí jen Chromium (~77 % zařízení); Safari ani iOS ho
 * nemají a podle všeho mít nebudou. Fronta proto běží NA POPŘEDÍ: odesílá
 * při startu aplikace, při návratu online a při návratu na kartu. Pro skladovou
 * aplikaci to stačí – skladník ji do minuty stejně otevře na další balík.
 * Kdyby se někdy jelo jen na Androidu, Background Sync se dá přidat jako
 * zrychlení, ne jako náhrada.
 *
 * IDEMPOTENCE
 * Každá položka nese `id` vygenerované klientem. To je klíč idempotence pro
 * backend: opakované odeslání téhož `id` nesmí vyrobit druhý záznam.
 * Pozor na rozdíl u zápisu skenu – opakovaný SKEN má vyrobit nový záznam
 * a zvýšit počítadlo; co nesmí vzniknout dvakrát, je jeden a tentýž sken
 * kvůli síťovému retry.
 */

export type QueueKind = 'scanLog' | 'reportIssue' | 'transferConfirm';

export type QueueState = 'pending' | 'failed';

export interface QueueItem {
  /** Client-side ID = klíč idempotence. Nemění se mezi pokusy. */
  id: string;
  kind: QueueKind;
  payload: unknown;
  createdAt: number;
  attempts: number;
  /** Dřív než v tento čas se položka nezkouší (exponenciální backoff). */
  nextAttemptAt: number;
  state: QueueState;
  lastError: string | null;
}

/** Úložiště je injektované, aby šlo frontu testovat bez IndexedDB. */
export interface QueueStorage {
  all(): Promise<QueueItem[]>;
  put(item: QueueItem): Promise<void>;
  remove(id: string): Promise<void>;
}

export type Sender = (payload: unknown, idempotencyKey: string) => Promise<void>;

export interface QueueOptions {
  storage: QueueStorage;
  senders: Partial<Record<QueueKind, Sender>>;
  now?: () => number;
  newId?: () => string;
  /** Po kolika neúspěšných pokusech položku označit jako `failed`. */
  maxAttempts?: number;
  /** Zařazení akce rovnou spustí odeslání. Vypíná se jen v testech. */
  autoFlush?: boolean;
}

const BASE_DELAY_MS = 5_000;
const MAX_DELAY_MS = 5 * 60_000;

export function backoffDelay(attempts: number): number {
  return Math.min(BASE_DELAY_MS * 2 ** (attempts - 1), MAX_DELAY_MS);
}

export function createQueue(opts: QueueOptions) {
  const now = opts.now ?? (() => Date.now());
  const newId = opts.newId ?? (() => crypto.randomUUID());
  const maxAttempts = opts.maxAttempts ?? 8;

  const autoFlush = opts.autoFlush ?? true;

  const listeners = new Set<(items: QueueItem[]) => void>();
  /** Právě běžící odesílání. Další volání `flush()` se na něj navěsí. */
  let running: Promise<void> | null = null;
  /** Někdo požádal o flush, zatímco jiný běžel – po dokončení se zopakuje. */
  let flushRequested = false;
  /** Příští kolo má ignorovat backoff (vrátil se signál). */
  let forceNext = false;

  async function notify(): Promise<void> {
    if (listeners.size === 0) return;
    const items = await opts.storage.all();
    for (const fn of listeners) fn(items);
  }

  async function enqueue(kind: QueueKind, payload: unknown): Promise<QueueItem> {
    const item: QueueItem = {
      id: newId(),
      kind,
      payload,
      createdAt: now(),
      attempts: 0,
      nextAttemptAt: 0,
      state: 'pending',
      lastError: null,
    };
    await opts.storage.put(item);
    await notify();
    // Odeslání se nečeká – UI potvrdí akci hned a fronta doběhne na pozadí.
    if (autoFlush) void flush();
    return item;
  }

  /**
   * Odeslat, co jde. Souběžná volání se navěsí na už běžící odesílání
   * (a vynutí si po něm ještě jedno kolo), takže položka nikdy neodejde dvakrát
   * a `await flush()` vždy počká na doběhnutí.
   *
   * `ignoreBackoff` použij při návratu online: backoff je od toho, aby aplikace
   * netloukla do serveru, ale když se právě vrátil signál, nemá smysl čekat
   * dalších pět minut. Skladník mezitím odejde s balíkem pryč.
   */
  function flush(options: { ignoreBackoff?: boolean } = {}): Promise<void> {
    if (options.ignoreBackoff) forceNext = true;
    if (running) {
      flushRequested = true;
      return running;
    }
    running = (async () => {
      do {
        flushRequested = false;
        const force = forceNext;
        forceNext = false;
        await flushOnce(force);
      } while (flushRequested);
    })().finally(() => {
      running = null;
    });
    return running;
  }

  async function flushOnce(ignoreBackoff = false): Promise<void> {
    const items = (await opts.storage.all())
      .filter((i) => i.state === 'pending' && (ignoreBackoff || i.nextAttemptAt <= now()))
      // FIFO – pořadí akcí ve skladu nese význam (sken → potvrzení převozu).
      .sort((a, b) => a.createdAt - b.createdAt);

    for (const item of items) {
      const send = opts.senders[item.kind];
      if (!send) {
        // Endpoint zatím neexistuje (viz FEATURES). Položku needáváme do
        // nekonečné smyčky – necháme ji čekat, dokud se sender nezaregistruje.
        continue;
      }

      try {
        await send(item.payload, item.id);
        await opts.storage.remove(item.id);
      } catch (err) {
        await handleFailure(item, err);
        // Při výpadku sítě nemá smysl pálit zbytek fronty hned teď.
        if (isTransient(err)) break;
      }
    }

    await notify();
  }

  async function handleFailure(item: QueueItem, err: unknown): Promise<void> {
    // 401 není chyba položky – uživatel je odhlášený. Nepočítáme pokus,
    // jinak by fronta „vyhořela" jen proto, že vypršel token.
    if (err instanceof ApiError && err.kind === 'UNAUTHORIZED') {
      await opts.storage.put({
        ...item,
        nextAttemptAt: now() + BASE_DELAY_MS,
        lastError: 'UNAUTHORIZED',
      });
      return;
    }

    // Trvalá chyba (400/404/422…) – opakování ji nespraví. Položku označíme
    // jako failed a necháme ji ve frontě k dohledání, nemažeme ji potichu.
    if (!isTransient(err)) {
      await opts.storage.put({
        ...item,
        attempts: item.attempts + 1,
        state: 'failed',
        lastError: describe(err),
      });
      return;
    }

    const attempts = item.attempts + 1;
    await opts.storage.put({
      ...item,
      attempts,
      state: attempts >= maxAttempts ? 'failed' : 'pending',
      nextAttemptAt: now() + backoffDelay(attempts),
      lastError: describe(err),
    });
  }

  function subscribe(fn: (items: QueueItem[]) => void): () => void {
    listeners.add(fn);
    void notify();
    return () => listeners.delete(fn);
  }

  async function list(): Promise<QueueItem[]> {
    return opts.storage.all();
  }

  /** Ruční „zkusit znovu" po chybě – vrátí failed položky zpět do hry. */
  async function retryFailed(): Promise<void> {
    const items = await opts.storage.all();
    await Promise.all(
      items
        .filter((i) => i.state === 'failed')
        .map((i) => opts.storage.put({ ...i, state: 'pending', attempts: 0, nextAttemptAt: 0 })),
    );
    await flush();
  }

  return { enqueue, flush, subscribe, list, retryFailed };
}

export type Queue = ReturnType<typeof createQueue>;

/** Síťová chyba a 5xx jsou dočasné; 4xx (kromě 401) opakování nespraví. */
export function isTransient(err: unknown): boolean {
  if (err instanceof ApiError) {
    if (err.kind === 'NETWORK') return true;
    if (err.status == null) return true;
    return err.status >= 500;
  }
  // Neznámou chybu raději zkusíme znovu, než abychom o hlášení přišli.
  return true;
}

function describe(err: unknown): string {
  if (err instanceof ApiError) return `${err.kind}${err.detail ? `: ${err.detail}` : ''}`;
  return err instanceof Error ? err.message : String(err);
}
